import { useState } from 'react'
import { Check, X } from 'lucide-react'
import type { Modifier } from '@/types'
import { getTranslation } from '@/lib/i18n'
import { formatPrice } from '@/lib/formatPrice'
import type { Language } from '@/types'

interface ModifierSelectorProps {
  itemId: string
  itemName: string
  basePrice: number
  modifiers: Modifier[]
  language: Language
  isRTL: boolean
  onAdd: (selectedModifiers: Array<{ id: string; optionId?: string; name: string; price: number }>) => void
  onClose: () => void
}

export function ModifierSelector({
  itemName,
  basePrice,
  modifiers,
  language,
  isRTL,
  onAdd,
  onClose,
}: ModifierSelectorProps) {
  const [selectedModifiers, setSelectedModifiers] = useState<Array<{
    id: string
    optionId?: string
    name: string
    price: number
  }>>([])

  const totalPrice = basePrice + selectedModifiers.reduce((sum, mod) => sum + mod.price, 0)

  const handleToggleModifier = (modifier: Modifier, optionId?: string) => {
    if (modifier.type === 'checkbox') {
      // Toggle checkbox modifier
      setSelectedModifiers((prev) => {
        const exists = prev.find((m) => m.id === modifier.id)
        if (exists) {
          return prev.filter((m) => m.id !== modifier.id)
        } else {
          return [
            ...prev,
            {
              id: modifier.id,
              name: getTranslation(
                { fr: modifier.name_fr, ar: modifier.name_ar, en: modifier.name_en },
                language
              ),
              price: modifier.price,
            },
          ]
        }
      })
    } else if (modifier.type === 'radio' && optionId) {
      // Select radio option
      const option = modifier.options?.find((opt) => opt.id === optionId)
      if (!option) return

      setSelectedModifiers((prev) => {
        // Remove any existing selection for this modifier
        const filtered = prev.filter((m) => m.id !== modifier.id)
        return [
          ...filtered,
          {
            id: modifier.id,
            optionId,
            name: getTranslation({ fr: option.name_fr, ar: option.name_ar, en: option.name_en }, language),
            price: option.price,
          },
        ]
      })
    }
  }

  const isModifierSelected = (modifierId: string, optionId?: string): boolean => {
    if (optionId) {
      return selectedModifiers.some((m) => m.id === modifierId && m.optionId === optionId)
    }
    return selectedModifiers.some((m) => m.id === modifierId)
  }

  const handleAdd = () => {
    onAdd(selectedModifiers)
  }

  const addButtonText =
    language === 'fr'
      ? 'Ajouter'
      : language === 'ar'
      ? 'إضافة'
      : 'Add'

  const cancelButtonText =
    language === 'fr' ? 'Annuler' : language === 'ar' ? 'إلغاء' : 'Cancel'

  const totalText =
    language === 'fr' ? 'Total' : language === 'ar' ? 'المجموع' : 'Total'

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
      <div
        className={`bg-white rounded-t-3xl w-full max-h-[80vh] overflow-hidden flex flex-col ${
          isRTL ? 'rtl' : ''
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold text-primary">{itemName}</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-secondary/20 transition-colors touch-feedback"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {modifiers.map((modifier) => (
            <div key={modifier.id}>
              <h4 className="font-semibold text-primary mb-3">
                {getTranslation(
                  { fr: modifier.name_fr, ar: modifier.name_ar, en: modifier.name_en },
                  language
                )}
              </h4>

              {modifier.type === 'checkbox' && (
                <button
                  onClick={() => handleToggleModifier(modifier)}
                  className={`
                    w-full flex items-center justify-between p-4 rounded-lg
                    border-2 transition-all touch-feedback
                    ${isModifierSelected(modifier.id)
                      ? 'border-primary bg-primary/10'
                      : 'border-secondary/30 hover:border-primary/50'
                    }
                  `}
                >
                  <span className="font-medium">
                    {getTranslation(
                      { fr: modifier.name_fr, ar: modifier.name_ar, en: modifier.name_en },
                      language
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    {modifier.price > 0 && (
                      <span className="text-sm text-muted-foreground">
                        +{formatPrice(modifier.price)}
                      </span>
                    )}
                    {isModifierSelected(modifier.id) && <Check size={18} className="text-primary" />}
                  </div>
                </button>
              )}

              {modifier.type === 'radio' && modifier.options && (
                <div className="space-y-2">
                  {modifier.options.map((option) => {
                    const isSelected = isModifierSelected(modifier.id, option.id)
                    const optionName = getTranslation(
                      { fr: option.name_fr, ar: option.name_ar, en: option.name_en },
                      language
                    )

                    return (
                      <button
                        key={option.id}
                        onClick={() => handleToggleModifier(modifier, option.id)}
                        className={`
                          w-full flex items-center justify-between p-4 rounded-lg
                          border-2 transition-all touch-feedback
                          ${isSelected
                            ? 'border-primary bg-primary/10'
                            : 'border-secondary/30 hover:border-primary/50'
                          }
                        `}
                      >
                        <span className="font-medium">{optionName}</span>
                        <div className="flex items-center gap-2">
                          {option.price > 0 && (
                            <span className="text-sm text-muted-foreground">
                              +{formatPrice(option.price)}
                            </span>
                          )}
                          {isSelected && <Check size={18} className="text-primary" />}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-secondary/10">
          <div className="flex items-center justify-between mb-4">
            <span className="font-semibold text-primary">{totalText}</span>
            <span className="text-2xl font-bold text-primary">{formatPrice(totalPrice)}</span>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-lg bg-secondary/30 text-primary font-medium hover:bg-secondary/50 transition-colors touch-feedback"
            >
              {cancelButtonText}
            </button>
            <button
              onClick={handleAdd}
              className="flex-1 py-3 px-4 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-colors touch-feedback"
            >
              {addButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
